import { useCallback, useMemo, useState } from 'react'

/*
 @see https://usehooks.com/useLocalStorage/
 Session Storage version
*/
export const useSessionStorage = <T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof globalThis.window === 'undefined') {
      return initialValue
    }

    try {
      // Get from session storage by key
      const item = globalThis.window.sessionStorage.getItem(key)
      
      if (!item) {
        globalThis.window.sessionStorage.setItem(key, JSON.stringify(initialValue))
      }

      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      // If error also return initialValue
      console.log(error)
      return initialValue
    }
  })

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  const setValue = useCallback(
    (value: T | ((val: T) => T)): void => {
      try {
        // Allow value to be a function, so we have same API as useState
        const valueToStore = value instanceof Function ? value(storedValue) : value
        // Save state
        setStoredValue(valueToStore)
        // Save to session storage
        if (typeof globalThis.window !== 'undefined') {
          globalThis.window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
        }
      } catch (error) {
        // A more advanced implementation would handle the error case
        console.log(error)
      }
    },
    [key, storedValue]
  )

  return useMemo(() => [storedValue, setValue], [storedValue, setValue])
}
